import type { HandKey, SignDictionary, SignEntry } from './types';

/**
 * Seed motion data.
 *
 * Every entry here is either (a) a pointing or non-manual form described in the
 * design handoff's own gloss key, or (b) a letter of the two-handed BANZSL
 * manual alphabet, authored from published per-letter prose descriptions —
 * primarily Deafblind Victoria's Auslan alphabet text description, corroborated
 * across BSL and Auslan sources. Each entry cites what it was authored from,
 * and where sources disagree the entry's note says so.
 *
 * Nothing else ships. Lexical Auslan signs are not guessed at: a wrong form
 * taught confidently is worse than an honest gap, so unknown signs report "no
 * motion data" and the stage says so. Filling the lexical dictionary is
 * Deaf-authored content work, not a coding task — see docs/signing-avatar.md.
 *
 * `reviewed` stays false on everything until a Deaf Auslan consultant signs the
 * form off. The stage surfaces that state on every token.
 */

const HANDOFF = 'Design handoff gloss key (design-handoff/.../README.md)';
const DBV =
  'Deafblind Victoria, Auslan alphabet text description (deafblindvictoria.com/auslan-alphabet-text-description)';
const LEAD = 'Lead Academy BSL alphabet guide (lead-academy.org/blog/british-sign-language-alphabet)';
const SUPERPROF = 'Superprof Auslan alphabet guide (superprof.com.au/blog/alphabet-sign-lang-oz)';
const WIKI = 'Wikipedia, “Two-handed manual alphabets” (vowel rule)';
const BANZSL_VOWELS = `${DBV}; ${LEAD}; ${WIKI}: the dominant index contacts the weak hand fingertip — thumb A, index E, middle I, ring O, little U.`;

/** Hands down, face neutral. Played between signs and when nothing is loaded. */
export const REST: SignEntry = {
  id: 'REST',
  source: 'Neutral rest position, not a sign.',
  reviewed: true,
  keyframes: [
    {
      t: 0,
      left: { shape: 'relaxed', location: 'rest', palm: 'in' },
      right: { shape: 'relaxed', location: 'rest', palm: 'in' },
    },
  ],
};

/** Held while the interpreter is still working. */
export const THINKING: SignEntry = {
  id: 'THINKING',
  source: 'Idle pose, not a sign.',
  reviewed: true,
  keyframes: [
    {
      t: 0,
      left: { shape: 'relaxed', location: 'rest', palm: 'in' },
      right: { shape: 'relaxed', location: 'chest', palm: 'in', aim: 'up' },
      nmf: { brow: 0.15 },
    },
    {
      t: 1,
      left: { shape: 'relaxed', location: 'rest', palm: 'in' },
      right: { shape: 'relaxed', location: 'chest', palm: 'in', aim: 'up', offset: [0, 0.05, 0.03] },
      nmf: { brow: 0.3 },
    },
  ],
};

const POINTING: SignEntry[] = [
  {
    id: 'PRO-1',
    source: HANDOFF,
    reviewed: false,
    note: 'Index points to the signer’s own chest — pointing, not a word.',
    keyframes: [
      {
        t: 0,
        right: { shape: 'point', location: 'neutral', palm: 'down', aim: 'in' },
      },
      {
        t: 0.45,
        right: { shape: 'point', location: 'self', palm: 'down', aim: 'in' },
      },
      {
        t: 1,
        right: { shape: 'point', location: 'self', palm: 'down', aim: 'in' },
      },
    ],
  },
  {
    id: 'PRO-2',
    source: HANDOFF,
    reviewed: false,
    note: 'Index points at the addressee.',
    keyframes: [
      {
        t: 0,
        right: { shape: 'point', location: 'chest', palm: 'down', aim: 'out' },
      },
      {
        t: 0.5,
        right: { shape: 'point', location: 'forward', palm: 'down', aim: 'out' },
      },
      {
        t: 1,
        right: { shape: 'point', location: 'forward', palm: 'down', aim: 'out' },
      },
    ],
  },
  {
    id: 'PRO-3',
    source: HANDOFF,
    reviewed: false,
    note: 'Index points to a third-person locus off to the side.',
    keyframes: [
      {
        t: 0,
        right: { shape: 'point', location: 'chest', palm: 'down', aim: 'out' },
      },
      {
        t: 0.5,
        right: {
          shape: 'point',
          location: 'forward',
          palm: 'down',
          aim: 'right',
          offset: [0.16, -0.04, -0.06],
        },
      },
      {
        t: 1,
        right: {
          shape: 'point',
          location: 'forward',
          palm: 'down',
          aim: 'right',
          offset: [0.16, -0.04, -0.06],
        },
      },
    ],
  },
];

/**
 * Q is a non-manual, not a handshape: the question is asked by the face. The
 * hands hold where they are while the brows furrow and the head tilts.
 */
const QUESTION: SignEntry = {
  id: 'Q',
  source: HANDOFF,
  reviewed: false,
  note: 'Question face: brows furrow, head tilts, hold.',
  keyframes: [
    {
      t: 0,
      left: { shape: 'relaxed', location: 'chest', palm: 'up', aim: 'out' },
      right: { shape: 'relaxed', location: 'chest', palm: 'up', aim: 'out' },
      nmf: { brow: 0, headTilt: 0 },
    },
    {
      t: 0.35,
      left: { shape: 'flat', location: 'neutral', palm: 'up', aim: 'out' },
      right: { shape: 'flat', location: 'neutral', palm: 'up', aim: 'out' },
      nmf: { brow: -1, headTilt: 0.4, headNod: 0.12 },
    },
    {
      t: 1,
      left: { shape: 'flat', location: 'neutral', palm: 'up', aim: 'out' },
      right: { shape: 'flat', location: 'neutral', palm: 'up', aim: 'out' },
      nmf: { brow: -1, headTilt: 0.4, headNod: 0.12 },
    },
  ],
};

/**
 * The weak hand is held up, fingers spread and pointing across the body toward
 * the dominant side, so the dominant index can contact each fingertip in turn.
 */
const WEAK_HAND: HandKey = {
  shape: 'spread',
  location: 'neutral',
  // Palm toward the signer, fingers up: the addressee sees the back of the hand
  // with the fingertips clear, which is what makes the contact readable.
  palm: 'in',
  aim: 'up',
  offset: [0.06, 0.06, 0.06],
};

function vowel(letter: string, contact: 'thumb' | 'index' | 'middle' | 'ring' | 'little'): SignEntry {
  return {
    id: letter,
    source: BANZSL_VOWELS,
    reviewed: false,
    note: `Dominant index contacts the weak hand’s ${contact} tip.`,
    keyframes: [
      {
        t: 0,
        left: { ...WEAK_HAND },
        right: { shape: 'point', location: 'chest', palm: 'out', aim: 'left' },
      },
      {
        t: 0.55,
        left: { ...WEAK_HAND },
        right: { shape: 'point', location: 'weakHand', palm: 'out', aim: 'left', contact },
      },
      {
        t: 1,
        left: { ...WEAK_HAND },
        right: { shape: 'point', location: 'weakHand', palm: 'out', aim: 'left', contact },
      },
    ],
  };
}

/** Weak hand open, palm up, fingers forward — the "paper" the L-group writes on. */
const WEAK_PALM_UP: HandKey = {
  shape: 'spread',
  location: 'neutral',
  palm: 'up',
  aim: 'out',
  // Lifted into the camera's framing: signing space, not the waist.
  offset: [0.08, 0.07, 0.06],
};

/** Weak index upright, palm toward the dominant side — the stem of D, K and P. */
const WEAK_POINT_UP: HandKey = {
  shape: 'point',
  location: 'neutral',
  palm: 'right',
  aim: 'up',
  offset: [0.06, 0.04, 0.04],
};

/**
 * A letter whose dominant hand travels from a ready position at the chest to a
 * contact (or contact path) on the weak hand, and holds. Two contacts make a
 * glide — that is H's wipe and J's trace.
 */
function letter(
  id: string,
  source: string,
  note: string,
  weak: HandKey,
  dominant: Omit<HandKey, 'location'>,
  path?: Array<Pick<HandKey, 'contact' | 'offset'>>,
): SignEntry {
  const steps = path ?? [{ contact: dominant.contact, offset: dominant.offset }];
  // The hand arrives at 0.55, travels any remaining path by 0.85, and holds to
  // 1. The hold is what a paused stage shows when a learner pins a letter, so
  // every letter must have one — including the single-contact majority.
  const arrive = 0.55;
  const settled = 0.85;
  const span = steps.length > 1 ? (settled - arrive) / (steps.length - 1) : 0;
  const contactFrame = (step: (typeof steps)[number], t: number) => ({
    t,
    left: { ...weak },
    right: { ...dominant, location: 'weakHand' as const, contact: step.contact, offset: step.offset },
  });
  const last = steps[steps.length - 1]!;
  return {
    id,
    source,
    reviewed: false,
    note,
    keyframes: [
      {
        t: 0,
        left: { ...weak },
        right: { ...dominant, location: 'chest', contact: undefined, offset: undefined },
      },
      ...steps.map((step, index) => contactFrame(step, arrive + span * index)),
      contactFrame(last, 1),
    ],
  };
}

/**
 * The two-handed BANZSL manual alphabet, complete. Authored from published
 * per-letter prose — Deafblind Victoria's Auslan text description first, with
 * BSL/Auslan corroboration — never from guesswork; the two letters where
 * sources disagree (D, S) follow the Auslan majority and say so in their note.
 */
const ALPHABET: Record<string, SignEntry> = {
  A: vowel('A', 'thumb'),
  E: vowel('E', 'index'),
  I: vowel('I', 'middle'),
  O: vowel('O', 'ring'),
  U: vowel('U', 'little'),

  /**
   * Symmetric, so it is authored as mirrored positions rather than as a
   * contact: the two rings meet edge to edge, which landing one hand's
   * fingertip *on* the other's would not produce.
   */
  B: {
    id: 'B',
    source: `${DBV}; ${LEAD}`,
    reviewed: false,
    note: 'Both hands make index–thumb circles, palms facing, the two rings touching — the "binoculars".',
    keyframes: [
      {
        t: 0,
        left: { shape: 'circle', location: 'chest', palm: 'right', aim: 'up' },
        right: { shape: 'circle', location: 'chest', palm: 'left', aim: 'up' },
      },
      {
        t: 0.55,
        left: { shape: 'circle', location: 'neutral', palm: 'right', aim: 'up', offset: [0.095, 0.06, 0.05] },
        right: { shape: 'circle', location: 'neutral', palm: 'left', aim: 'up', offset: [-0.095, 0.06, 0.05] },
      },
      {
        t: 1,
        left: { shape: 'circle', location: 'neutral', palm: 'right', aim: 'up', offset: [0.095, 0.06, 0.05] },
        right: { shape: 'circle', location: 'neutral', palm: 'left', aim: 'up', offset: [-0.095, 0.06, 0.05] },
      },
    ],
  },
  C: {
    id: 'C',
    source: `${DBV}; ${LEAD}; ${SUPERPROF}`,
    reviewed: false,
    note: 'Dominant index and thumb curve into an open C, other fingers in a fist; the weak hand rests.',
    keyframes: [
      { t: 0, right: { shape: 'cee', location: 'chest', palm: 'left', aim: 'up' } },
      { t: 0.5, right: { shape: 'cee', location: 'neutral', palm: 'left', aim: 'up', offset: [0, 0.08, 0.06] } },
      { t: 1, right: { shape: 'cee', location: 'neutral', palm: 'left', aim: 'up', offset: [0, 0.08, 0.06] } },
    ],
  },
  D: letter(
    'D',
    `${DBV}; ${SUPERPROF}`,
    'Weak index upright; dominant index and thumb arc against it, completing the D. ' +
      'Sources conflict: one BSL guide puts the upright stroke on the middle finger — Auslan majority followed here.',
    WEAK_POINT_UP,
    { shape: 'cee', palm: 'left', aim: 'up', contact: 'index', offset: [0.01, -0.01, 0] },
  ),
  F: letter(
    'F',
    `${DBV}; ${LEAD}`,
    'Both hands with index and middle extended together, palms down; dominant fingers cross over the back of the weak fingers.',
    { shape: 'two', location: 'neutral', palm: 'down', aim: 'out', offset: [0.08, 0.06, 0.08] },
    // The dominant pair comes in from the side so the cross reads face-on.
    { shape: 'two', palm: 'down', aim: 'left', contact: 'index', offset: [0.01, 0.02, 0] },
  ),
  G: letter(
    'G',
    `${DBV}; ${LEAD}`,
    'Both fists, palms toward the signer; the dominant fist stacks on top of the weak fist.',
    { shape: 'fist', location: 'neutral', palm: 'in', aim: 'right', offset: [0.1, 0.02, 0.04] },
    { shape: 'fist', palm: 'in', aim: 'left', contact: 'fist', offset: [0, 0.02, 0] },
  ),
  H: letter(
    'H',
    `${DBV}; ${LEAD}`,
    'Weak flat hand palm up; dominant flat hand palm down wipes forward along it, from the heel off past the fingertips. One of the alphabet’s two movement letters.',
    { shape: 'flat', location: 'neutral', palm: 'up', aim: 'out', offset: [0.08, 0.07, 0.06] },
    { shape: 'flat', palm: 'down', aim: 'out' },
    [
      { contact: 'heel', offset: [0, 0.02, 0] },
      { contact: 'palm', offset: [0, 0.015, 0] },
      // The wipe ends just clear of the fingertips, which is also the hold.
      { contact: 'index', offset: [0, 0.02, 0.025] },
    ],
  ),
  J: letter(
    'J',
    `${DBV}; ${LEAD}; ${SUPERPROF}`,
    'The dominant index traces a J: down from the weak middle fingertip, through the base of the hand, up the thumb. The alphabet’s other movement letter.',
    WEAK_HAND,
    { shape: 'point', palm: 'out', aim: 'left' },
    [
      { contact: 'middle', offset: undefined },
      { contact: 'heel', offset: [0, 0, 0.025] },
      { contact: 'thumb', offset: undefined },
    ],
  ),
  K: letter(
    'K',
    `${DBV}; ${LEAD}; ${SUPERPROF}`,
    'Weak index upright; the dominant index bends into a hook and its knuckle rests against the middle of the weak index.',
    WEAK_POINT_UP,
    // The knuckle rests mid-finger, so the contact sits below the weak fingertip.
    { shape: 'hook', palm: 'down', aim: 'left', contact: 'index', offset: [0, -0.05, 0.01] },
  ),
  L: letter(
    'L',
    `${DBV}; ${LEAD}`,
    'Weak hand open palm up; the dominant index lies flat along the middle of the palm.',
    WEAK_PALM_UP,
    { shape: 'point', palm: 'down', aim: 'out', contact: 'palm', offset: [0, 0.005, 0] },
  ),
  M: letter(
    'M',
    `${DBV}; ${LEAD}`,
    'Three dominant fingers — index, middle, ring — lie flat on the weak palm: three strokes of the M.',
    WEAK_PALM_UP,
    { shape: 'three', palm: 'down', aim: 'out', contact: 'palm', offset: [0, 0.005, 0] },
  ),
  N: letter(
    'N',
    `${DBV}; ${LEAD}`,
    'Two dominant fingers — index and middle together — lie flat on the weak palm.',
    WEAK_PALM_UP,
    { shape: 'two', palm: 'down', aim: 'out', contact: 'palm', offset: [0, 0.005, 0] },
  ),
  P: letter(
    'P',
    `${DBV}; ${LEAD}; ${SUPERPROF}`,
    'Weak index upright; the dominant index–thumb ring touches its tip — the bowl of the P on its stem.',
    WEAK_POINT_UP,
    { shape: 'circle', palm: 'left', aim: 'up', contact: 'index', offset: [0, 0, 0.01] },
  ),
  Q: letter(
    'Q',
    `${DBV}; ${LEAD}`,
    'Weak hand makes the index–thumb circle; the dominant index hooks forward onto its thumb — the Q’s tail.',
    { shape: 'circle', location: 'neutral', palm: 'right', aim: 'up', offset: [0.06, 0.04, 0.04] },
    { shape: 'hook', palm: 'out', aim: 'down', contact: 'thumb', offset: [0, 0.01, 0.01] },
  ),
  R: letter(
    'R',
    `${DBV}; ${LEAD}`,
    'The dominant index curls into a hook and its side rests on the open weak palm.',
    WEAK_PALM_UP,
    // Palm toward the signer, fingers across the body: 'out' would be
    // antiparallel to the palm normal and describe no rotation at all.
    { shape: 'hook', palm: 'in', aim: 'left', contact: 'palm', offset: [0, 0.012, 0] },
  ),
  S: letter(
    'S',
    `${DBV}`,
    'Little finger to little finger: the dominant little fingertip touches the weak one. ' +
      'A BSL variant interlocks the two little fingers instead — Auslan source followed here.',
    WEAK_HAND,
    { shape: 'littleOut', palm: 'down', aim: 'left', contact: 'little', touchWith: 'little' },
  ),
  T: letter(
    'T',
    `${DBV}; ${LEAD}; ${SUPERPROF}`,
    'The dominant index fingertip touches the base of the weak palm.',
    { shape: 'spread', location: 'neutral', palm: 'right', aim: 'up', offset: [0.06, 0.04, 0.04] },
    { shape: 'point', palm: 'down', aim: 'left', contact: 'heel', offset: [0.01, 0, 0.01] },
  ),
  V: letter(
    'V',
    `${DBV}; ${LEAD}`,
    'Dominant index and middle spread into a V and lie on the open weak palm.',
    WEAK_PALM_UP,
    { shape: 'vee', palm: 'down', aim: 'out', contact: 'palm', offset: [0, 0.005, 0] },
  ),
  W: letter(
    'W',
    `${DBV}; ${LEAD}`,
    'Both hands open, palms facing, fingers interlaced and pointing up. The rig meshes the hands rather than truly interlocking the fingers.',
    { shape: 'spread', location: 'neutral', palm: 'right', aim: 'up', offset: [0.1, 0.06, 0.06] },
    { shape: 'spread', palm: 'left', aim: 'up', contact: 'palm', offset: [0.015, 0.02, 0.01] },
  ),
  X: letter(
    'X',
    `${DBV}; ${LEAD}`,
    'Both index fingers extended, palms down, the dominant crossing over the back of the weak — an X.',
    { shape: 'point', location: 'neutral', palm: 'down', aim: 'out', offset: [0.07, 0.08, 0.08] },
    // Crossed at mid-finger, dominant coming from the side so the X is face-on.
    { shape: 'point', palm: 'down', aim: 'left', contact: 'index', offset: [0.01, 0.018, -0.03] },
  ),
  Y: letter(
    'Y',
    `${DBV}; ${LEAD}`,
    'The dominant index lies in the webbing between the weak thumb and index, both palms forward.',
    { shape: 'spread', location: 'neutral', palm: 'out', aim: 'up', offset: [0.08, 0.05, 0.02] },
    { shape: 'point', palm: 'out', aim: 'left', contact: 'web', offset: [0, 0.01, 0.01] },
  ),
  Z: letter(
    'Z',
    `${DBV}; ${LEAD}`,
    'The dominant hand bends flat at the knuckles and its fingertips touch the middle of the upright weak palm.',
    { shape: 'flat', location: 'neutral', palm: 'right', aim: 'up', offset: [0.06, 0.04, 0.04] },
    { shape: 'flatBent', palm: 'left', aim: 'out', contact: 'palm' },
  ),
};

export const DICTIONARY: SignDictionary = {
  signs: [...POINTING, QUESTION],
  alphabet: ALPHABET,
};
